import { Link } from "react-router-dom";
import { AvatarPair } from "../../components/Avatar.js";
import { formatScoreline } from "../../lib/matches.js";
import type { DrawFixture, TournamentEvent } from "../../lib/types.js";
import { EventGate, rosterOf, sideNames, sidePeople, useDraw, type Roster } from "./shared.js";

/** How often the board re-reads the draw. Long enough to be idle, short enough to keep up. */
const REFRESH_MS = 15_000;

/**
 * The board for the screen on the wall — requirements §5.3's "next up".
 *
 * Read-only, no navigation, and outside the app shell: a television does not need a bottom
 * tab bar, and the thing that is on court should be readable from the far end of the hall.
 *
 * It has no court assignment in it, and that is not an omission. The club plays on one
 * court, which is why the courts field came off the event form — so "which court" has one
 * answer and the queue is simply the draw in its order. When a second court arrives, this is
 * the screen that grows a column.
 *
 * It polls rather than subscribes. Scores are entered on somebody's phone at the other end
 * of the hall and this has nobody to touch it, so it re-reads every fifteen seconds; a
 * realtime subscription would be one more thing to keep alive for a gain nobody can see at
 * this cadence.
 */
export function EventBoard() {
  return <EventGate>{({ event }) => <Board event={event} />}</EventGate>;
}

function Board({ event }: { event: TournamentEvent }) {
  const { data: fixtures = [], isPending } = useDraw(event.id, { refetchInterval: REFRESH_MS });
  const roster = rosterOf(event);

  const unplayed = fixtures.filter((fixture) => !fixture.result);
  const [onCourt, ...queue] = unplayed;
  const played = fixtures.length - unplayed.length;

  const latest = fixtures
    .filter((fixture) => fixture.result)
    .sort((x, y) => (y.result?.playedAt ?? "").localeCompare(x.result?.playedAt ?? ""))[0];

  return (
    // Floodlit: the one screen that keeps the dark palette. It hangs on a wall at the far
    // end of a hall, and light type on a dark ground carries further than paper does.
    <div className="floodlit min-h-dvh">
      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-8 flex items-baseline justify-between gap-4">
          <h1 className="micro">{event.name}</h1>
          <Link to={`/events/${event.id}`} className="micro underline">
            Leave the board
          </Link>
        </div>

        {fixtures.length === 0 ? (
          <p className="text-ink-muted">
            {isPending
              ? "Loading…"
              : // An auction pool is never drawn — its teams come from the auction — so
                // "nothing drawn yet" promised a draw that was never coming.
                event.format === "auction_pool"
                ? "Teams are picked at the auction, so this event has no draw to show."
                : "Nothing drawn yet."}
          </p>
        ) : onCourt ? (
          <>
            <p className="mb-4">
              <span className="highlight font-mono px-2.5 py-1.5 text-sm font-semibold tracking-[0.14em]">
                ON COURT
              </span>
            </p>
            <p
              className="font-display leading-tight"
              style={{
                fontSize: "clamp(2.5rem, 9vw, 6.5rem)",
                fontWeight: 900,
                textTransform: "uppercase",
                lineHeight: 0.86,
              }}
            >
              {sideNames(onCourt.playersA, roster)}
            </p>
            <p className="micro my-2">v</p>
            <p
              className="font-display leading-tight"
              style={{
                fontSize: "clamp(2.5rem, 9vw, 6.5rem)",
                fontWeight: 900,
                textTransform: "uppercase",
                lineHeight: 0.86,
              }}
            >
              {sideNames(onCourt.playersB, roster)}
            </p>

            {queue.length > 0 ? (
              <section
                className="mt-10 border-t-2 pt-4"
                style={{ borderColor: "var(--color-line)" }}
              >
                <p className="micro mb-2">Next up</p>
                <ol>
                  {queue.slice(0, 4).map((fixture) => (
                    <li
                      key={fixture.id}
                      className="flex items-baseline justify-between gap-4 border-t py-2"
                      style={{ borderColor: "var(--color-line-muted)" }}
                    >
                      <span
                        className="flex items-center gap-2"
                        style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)" }}
                      >
                        <AvatarPair people={sidePeople(fixture.playersA, roster)} size={26} />
                        {sideNames(fixture.playersA, roster)} <span className="micro">v</span>{" "}
                        <AvatarPair people={sidePeople(fixture.playersB, roster)} size={26} />
                        {sideNames(fixture.playersB, roster)}
                      </span>
                      <span className="micro shrink-0">#{fixture.sequence}</span>
                    </li>
                  ))}
                </ol>
                {queue.length > 4 ? (
                  <p className="micro mt-2">and {queue.length - 4} more after that</p>
                ) : null}
              </section>
            ) : null}
          </>
        ) : (
          <p className="font-display text-3xl" style={{ fontWeight: 800 }}>
            That&rsquo;s the lot — every match has been played.
          </p>
        )}

        {/* Only once there is something to count: "0 of 0 played" under an empty board is
            a number about nothing. */}
        {fixtures.length > 0 ? (
          <footer className="mt-10 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
            <p className="micro">
              {played} of {fixtures.length} played
            </p>
            {latest ? <Last fixture={latest} roster={roster} /> : null}
          </footer>
        ) : null}
      </main>
    </div>
  );
}

/** The score that landed most recently — the thing people look up to check. */
function Last({ fixture, roster }: { fixture: DrawFixture; roster: Roster }) {
  const result = fixture.result;
  if (!result) return null;

  const winner = result.winner === "A" ? fixture.playersA : fixture.playersB;
  const loser = result.winner === "A" ? fixture.playersB : fixture.playersA;

  return (
    <p className="mt-2" style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)" }}>
      {sideNames(winner, roster)}{" "}
      <span className="font-mono">
        {formatScoreline(result.games, result.winner === "A" ? "A" : "B")}
      </span>{" "}
      <span className="text-ink-muted">{sideNames(loser, roster)}</span>
    </p>
  );
}
